import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { VendedorRecipeResult } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { platformAdminApi } from '../api/platformAdminApi';
import { useActiveTenants } from './recipe.hooks';
import { ClientFields, Question, RecipeDone, RecipeFooter, RecipePage } from './recipeParts';
import { defaultSellerName, parseSizeMin, previewQuestions } from './vendedorRecipe.utils';

export function VendedorRecipePage() {
  const [error, setError] = useState('');
  const tenants = useActiveTenants(setError);
  const [tenantId, setTenantId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [agentName, setAgentName] = useState('');
  const [productName, setProductName] = useState('');
  const [productSummary, setProductSummary] = useState('');
  const [sizeQuestion, setSizeQuestion] = useState('');
  const [sizeMinRaw, setSizeMinRaw] = useState('');
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState<VendedorRecipeResult | null>(null);

  const answered = [
    Boolean(tenantId && companyName.trim()),
    Boolean(productName.trim() && productSummary.trim()),
    Boolean(sizeQuestion.trim()),
  ];
  const ready = answered.every(Boolean);
  const sizeMin = parseSizeMin(sizeMinRaw);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || progress) return;
    setError('');
    setProgress('Creando el Vendedor');
    try {
      const created = await platformAdminApi.createVendedor({
        tenantId,
        companyName: companyName.trim(),
        agentName: agentName.trim() || undefined,
        productName: productName.trim(),
        productSummary: productSummary.trim(),
        sizeQuestion: sizeQuestion.trim(),
        sizeMin,
      });
      setResult(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el Vendedor');
    } finally {
      setProgress('');
    }
  };

  if (result) {
    return (
      <RecipeDone
        title={`El Vendedor de ${companyName} está creado`}
        subtitle='Atiende, averigua y pasa a ventas a los que valen la pena. Falta conectarle el WhatsApp donde llegan los interesados.'
        steps={result.steps}
        aiReady={result.aiReady}
        actions={
          <>
            <Button asChild size='lg'>
              <Link to='/admin/whatsapp'>Conectar WhatsApp</Link>
            </Button>
            <Button asChild size='lg' variant='outline'>
              <Link to='/admin/leads/board'>Ver interesados</Link>
            </Button>
          </>
        }
      />
    );
  }

  return (
    <RecipePage
      title='Nuevo Vendedor'
      intro='Un agente que atiende a cada interesado, averigua lo que importa y le pasa los buenos al equipo. Tres respuestas y queda listo.'
    >
      <form onSubmit={handleSubmit} noValidate>
        <ol className='relative'>
          <Question done={answered[0]} title='¿Para qué cliente?' hint='El Vendedor queda dentro de su cuenta.'>
            <ClientFields
              tenants={tenants}
              tenantId={tenantId}
              onTenant={tenant => {
                setTenantId(tenant.id);
                setCompanyName(tenant.name);
              }}
              companyName={companyName}
              onCompanyName={setCompanyName}
              agentName={agentName}
              onAgentName={setAgentName}
              defaultAgentName={defaultSellerName(companyName)}
            />
          </Question>

          <Question
            done={answered[1]}
            title='¿Qué vende?'
            hint='El agente lo usa para explicar qué es y para qué sirve cuando le preguntan.'
          >
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='product'>Producto o servicio</Label>
                <Input
                  id='product'
                  value={productName}
                  maxLength={120}
                  placeholder='Rastreo satelital de flotas'
                  onChange={event => setProductName(event.target.value)}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='summary'>Contalo como se lo contarías a un cliente</Label>
                <Textarea
                  id='summary'
                  rows={4}
                  maxLength={600}
                  value={productSummary}
                  placeholder={'Ves dónde está cada camión en tiempo real.\nTe avisa si se desvía o para donde no debe.'}
                  onChange={event => setProductSummary(event.target.value)}
                />
                <p className='text-xs text-muted-foreground'>Una idea por línea.</p>
              </div>
            </div>
          </Question>

          <Question
            done={answered[2]}
            last
            title='¿Cuándo vale la pena un interesado?'
            hint='La pregunta que te dice si es un cliente para vos. Los que llegan al mínimo pasan a ventas.'
          >
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='sizeQuestion'>La pregunta</Label>
                <Input
                  id='sizeQuestion'
                  value={sizeQuestion}
                  maxLength={500}
                  placeholder='¿Cuántos vehículos tiene tu empresa?'
                  onChange={event => setSizeQuestion(event.target.value)}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='sizeMin'>Desde cuánto pasarlo a ventas</Label>
                <Input
                  id='sizeMin'
                  inputMode='numeric'
                  className='sm:w-40'
                  value={sizeMinRaw}
                  placeholder='10'
                  onChange={event => setSizeMinRaw(event.target.value)}
                />
                <p className='text-xs text-muted-foreground'>
                  {sizeMin ? `Los que respondan menos de ${sizeMin} quedan para revisar.` : 'Vacío: pasa a ventas todo el que conteste.'}
                </p>
              </div>
              {answered[1] || sizeQuestion.trim() ? (
                <ConversationPreview questions={previewQuestions(productName, sizeQuestion)} />
              ) : null}
            </div>
          </Question>
        </ol>

        <RecipeFooter error={error} progress={progress} answered={answered} submitLabel='Crear Vendedor' />
      </form>
    </RecipePage>
  );
}

/** Lo que va a preguntar el agente, en orden: hace concreto lo que se está configurando. */
function ConversationPreview({ questions }: { questions: string[] }) {
  return (
    <figure className='rounded-lg bg-muted/50 p-4'>
      <figcaption className='mb-3 text-sm text-muted-foreground'>Así va a arrancar la charla:</figcaption>
      <ol className='space-y-2' aria-label='Preguntas del agente'>
        {questions.map(question => (
          <li
            key={question}
            className='w-fit max-w-[85%] rounded-2xl rounded-tl-sm bg-background px-3 py-2 text-sm text-foreground shadow-sm'
          >
            {question}
          </li>
        ))}
      </ol>
    </figure>
  );
}
